// LanguagePicker (UI-C2, D-04, D-06, D-21): a searchable list, Arabic, English and Chinese pinned, the rest
// alphabetical by the name in the UI language. Each row shows the name in the UI language and the
// language's own name (with its lang attribute). No claim about any language's quality.
import { useMemo, useState } from "react";
import { languageName, pickerOrder, searchLanguages } from "../data/languages";
import type { Language } from "../data/speakLanguages";
import { dirOf, useI18n } from "../i18n";
import { CheckIcon } from "./icons";
import { Sheet } from "./Sheet";

export function LanguagePicker({
  title,
  list,
  value,
  onPick,
  onClose,
  spec,
  notes = [],
  error = null,
  pending = null,
  pendingLabel = "",
}: {
  title: string;
  list: readonly Language[];
  /** The row with the check: what the SERVER recorded (the speak picker never moves it before seat_updated). */
  value: string;
  onPick: (tag: string) => void;
  onClose: () => void;
  spec: string;
  /** MAJLIS-44 (the speak picker, D-34): lines under the title that say when and for whom the choice applies. */
  notes?: readonly string[];
  /** MAJLIS-44: the refusal line (form-error style, role=alert); null: none. */
  error?: string | null;
  /** MAJLIS-44: the row asked for and not yet confirmed (aria-busy, with `pendingLabel` beside its name). */
  pending?: string | null;
  pendingLabel?: string;
}) {
  const { lang, t } = useI18n();
  const [q, setQ] = useState("");
  const rows = useMemo(() => searchLanguages(pickerOrder(list, lang), q, lang), [list, lang, q]);
  const searchId = `search-${spec.replace(/\W/g, "")}`;
  return (
    <Sheet title={title} onClose={onClose} spec={spec}>
      {notes.map((n) => (
        <p key={n} className="hint picker-note">
          {n}
        </p>
      ))}
      {error && (
        <p className="form-error picker-error" role="alert">
          {error}
        </p>
      )}
      <label className="visually-hidden" htmlFor={searchId}>
        {t("searchLanguages")}
      </label>
      <input
        id={searchId}
        className="text-input"
        type="search"
        dir="auto"
        placeholder={t("searchLanguages")}
        autoComplete="off"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <ul className="lang-list" aria-label={title}>
        {rows.map((l) => (
          <li key={l.tag}>
            <button
              type="button"
              className="lang-option"
              aria-pressed={l.tag === value}
              aria-busy={l.tag === pending ? true : undefined}
              data-tag={l.tag}
              onClick={() => onPick(l.tag)}
            >
              <span className="lang-name">{languageName(l.tag, lang)}</span>
              {l.tag === pending && <span className="lang-pending">{pendingLabel}</span>}
              {l.native !== languageName(l.tag, lang) && (
                <span className="lang-native" lang={l.tag} dir={dirOf(l.tag)}>
                  {l.native}
                </span>
              )}
              {l.tag === value && <CheckIcon />}
            </button>
          </li>
        ))}
        {rows.length === 0 && <li className="lang-empty">{t("noLanguage")}</li>}
      </ul>
    </Sheet>
  );
}
